import React, { useState, useEffect, useRef } from "react";
import {
  Typography, Box, Paper, TextField, IconButton,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, CircularProgress, Grid, Chip, Button, Tooltip
} from "@mui/material";
import { Refresh, Visibility, Delete, Print, Save, Edit } from "@mui/icons-material";
import { TableComponent } from "../../components/Table";
import { GridColDef, GridPaginationModel } from "@mui/x-data-grid";
import { useNotification } from "../../context/notification.context";
import { product } from "../../api/paquetes";
import { proveedores } from "../../api/proveedores";
import logo from "../../assets/logo.png";
import { FinancialSummary, PaymentsSection } from "./Sections/FinancePayments";
import { PassengersSection } from "./Sections/PassengersSection";
import { ObservationsSection } from "./Sections/ObservationsSection";
import { ServicesList } from "./Sections/ServicesList";
import { printPackage } from "./Sections/PrintTemplate";
import { mapAllServicios, mapPagos, mapPasajeros, parseObservaciones, generateAutoObservaciones } from "./Sections/PackageDataMapper";

interface ProductosProps {
  terminoBusqueda?: string;
  refreshTrigger?: number;
  openPackageId?: number;
  onCloseFicha?: () => void;
}

const Productos: React.FC<ProductosProps> = ({ terminoBusqueda = '', refreshTrigger = 0, openPackageId, onCloseFicha }) => {
  const getCurrentUsername = () => {
    try {
      const u = (localStorage.getItem("current_username") || "").trim();
      return u || "SISTEMA";
    } catch {
      return "SISTEMA";
    }
  };

  const todayISO = () => new Date().toISOString().split('T')[0];

  const buildObs = (descripcion: string, usuario?: string, fecha?: string) => ({
    descripcion,
    fecha: fecha || todayISO(),
    usuario: (usuario || '').trim() || getCurrentUsername(),
  });

  const originalPaqueteRef = useRef<{ concepto?: string; descripcion?: string; precioVenta?: number; moneda?: string } | null>(null);

  // Estados principales
  const [productos, setProductos] = useState<any[]>([]);
  const [productosFiltrados, setProductosFiltrados] = useState<any[]>([]);
  const [totalProductos, setTotalProductos] = useState(0);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({ page: 0, pageSize: 100 });
  const [cargando, setCargando] = useState(true);
  
  // Estados para modales
  const [dialogoAbierto, setDialogoAbierto] = useState(false);
  const [productoAEliminar, setProductoAEliminar] = useState<number | null>(null);
  const [fichaAbierta, setFichaAbierta] = useState(false);
  const [productoSeleccionado, setProductoSeleccionado] = useState<any>(null);
  
  // Contexto de notificaciones
  const { getError, getSuccess } = useNotification();

  // Estados para formularios de la ficha
  const [nuevoPasajero, setNuevoPasajero] = useState({ nombre: "", dni: "", pasaporte: "", fechaNacimiento: "" });
  const [nuevoPago, setNuevoPago] = useState({ concepto: "", moneda: "USD", monto: "", tipoCambio: "", responsable_nombre: getCurrentUsername(), revisado_por_nombre: "" });
  const [nuevaObservacion, setNuevaObservacion] = useState(() => ({ descripcion: "", fecha: "", usuario: getCurrentUsername() }));

  const [guardandoCambios, setGuardandoCambios] = useState(false);
  const [sincronizandoProveedores, setSincronizandoProveedores] = useState(false);

  const getSyncEligibleCountFromServicios = (servicios: any[]): number => {
    return servicios.filter((s: any) => {
      const proveedorId = s?.proveedorId ?? s?.proveedor_id;
      const hasProviderId = Number.isFinite(Number(proveedorId)) && Number(proveedorId) > 0;
      const hasProviderName = (s?.proveedor || "").toString().trim().length > 0;
      const hasProvider = hasProviderId || hasProviderName;
      const hasFile = ((s?.nroFile ?? s?.nro_file) || "").toString().trim().length > 0;
      const costo = Number(s?.precioCosto ?? s?.importe_costo ?? 0);
      const hasCosto = Number.isFinite(costo) && costo > 0;
      return hasProvider && hasFile && hasCosto;
    }).length;
  };

  const getSyncEligibleCount = (): number => getSyncEligibleCountFromServicios(productoSeleccionado?.servicios || []);

  const buildUpdatePayload = (paquete: any, observacionesOverride?: any[]) => {
    const servicios = paquete?.servicios || [];
    const observaciones = Array.isArray(observacionesOverride)
      ? observacionesOverride
      : (Array.isArray(paquete?.observaciones) ? paquete.observaciones : []);

    const observacionesString = observaciones.length > 0 ? JSON.stringify(observaciones) : null;

    const aereosPaquetes = servicios
      .filter((s: any) => s.tipo === 'Aéreo')
      .map((s: any) => ({
        id: s.id,
        descripcion: s.descripcion,
        importe: s.precioVenta,
        moneda: s.moneda,
        importe_costo: s.precioCosto,
        moneda_costo: s.monedaCosto,
        proveedor_id: s.proveedorId ?? s.proveedor_id ?? null,
        nro_file: s.nroFile ?? s.nro_file,
        revisado: s.revisado,
        revisado_costo: s.revisadoCosto,
        fecha_viaje: s.fechaViaje || null
      }));

    const hotelesPaquetes = servicios
      .filter((s: any) => s.tipo === 'Hotel')
      .map((s: any) => ({
        id: s.id,
        descripcion: s.descripcion,
        noches: s.noches,
        servicios_incluidos: s.serviciosIncluidos,
        importe: s.precioVenta,
        moneda: s.moneda,
        importe_costo: s.precioCosto,
        moneda_costo: s.monedaCosto,
        proveedor_id: s.proveedorId ?? s.proveedor_id ?? null,
        nro_file: s.nroFile ?? s.nro_file,
        revisado: s.revisado,
        revisado_costo: s.revisadoCosto,
        fecha_viaje: s.fechaViaje || null
      }));

    const serviciosPaquetes = servicios
      .filter((s: any) => s.tipo === 'Extra')
      .map((s: any) => ({
        id: s.id,
        descripcion: s.descripcion,
        importe: s.precioVenta,
        moneda: s.moneda,
        importe_costo: s.precioCosto,
        moneda_costo: s.monedaCosto,
        proveedor_id: s.proveedorId ?? s.proveedor_id ?? null,
        nro_file: s.nroFile ?? s.nro_file,
        revisado: s.revisado,
        revisado_costo: s.revisadoCosto,
        fecha_viaje: s.fechaViaje || null
      }));

    return {
      concepto: paquete?.concepto,
      descripcion: paquete?.descripcion,
      importe: paquete?.precioVenta,
      moneda: paquete?.moneda,
      observaciones: observacionesString,
      aereosPaquetes,
      hotelesPaquetes,
      serviciosPaquetes,
    };
  };

  const persistPaqueteConSync = async (
    paquete: any,
    options?: { closeAfterSave?: boolean; noEligibleMessage?: string; }
  ) => {
    const closeAfterSave = options?.closeAfterSave ?? false;
    const noEligibleMessage = options?.noEligibleMessage || "Cambios guardados exitosamente";

    const payload = buildUpdatePayload(paquete);
    await product.update(paquete.id, payload);

    const elegibles = getSyncEligibleCountFromServicios(paquete?.servicios || []);
    if (elegibles > 0) {
      setSincronizandoProveedores(true);
      try {
        const syncResponse = await product.syncProviders(paquete.id);
        const result = syncResponse.data || {};
        if (Number(result.total || 0) === 0) {
          getSuccess("Cambios guardados");
          getSuccess(result.message || "No hay lineas elegibles para sincronizar (requiere proveedor valido + nro file)");
        } else {
          getSuccess(
            `Guardado y enviado a proveedores. Creados: ${result.created || 0}, Actualizados: ${result.updated || 0}, Omitidos: ${result.skipped || 0}.`
          );
        }
      } catch (syncError: any) {
        getSuccess("Cambios guardados, pero fallo el envio a proveedores");
        const syncMessage = syncError?.response?.data?.message || "No se pudo sincronizar con Proveedores";
        getError(syncMessage);
      } finally {
        setSincronizandoProveedores(false);
      }
    } else {
      getSuccess(noEligibleMessage);
    }

    const productosActualizados = productos.map(p =>
      p.id === paquete.id ? paquete : p
    );
    setProductos(productosActualizados);
    setProductosFiltrados(productosActualizados);

    originalPaqueteRef.current = {
      concepto: (paquete?.concepto ?? '').toString(),
      descripcion: (paquete?.descripcion ?? '').toString(),
      precioVenta: Number(paquete?.precioVenta || 0),
      moneda: (paquete?.moneda ?? 'USD').toString(),
    };

    if (closeAfterSave) {
      setFichaAbierta(false);
    }
  };

  // Proveedores cargados desde API para usar en agregar servicios
  const [proveedoresLista, setProveedoresLista] = useState<Array<{ id: number; nombre: string }>>([]);

  // Ref estable para getError (evita que cargarProductos se recree en cada toast)
  const getErrorRef = useRef(getError);
  getErrorRef.current = getError;

  // Función helper para formatear moneda
  const formatearMoneda = (valor: number, moneda: string = 'ARS'): string => {
    if (moneda === 'ARS') {
      return `$ ${valor.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    return `U$D ${valor.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Función para cargar productos (reutilizable)
  const cargarProductos = React.useCallback(async (page: number, limit: number, searchTerm: string = "") => {
    try {
      setCargando(true);
      const response = await product.getAll(page, limit, searchTerm);
      // La API devuelve los datos en response.data.data
      const payload = response.data;
      const paquetesAPI = Array.isArray(payload?.data) ? payload.data : (Array.isArray(payload) ? payload : []);
      const total = response.data?.pagination?.total;
      
      // Transformar datos de la API al formato del componente
      const paquetes = paquetesAPI
        .map((p: any) => ({
          id: p.id,
          numero: p.id,
          estado: p.estado || "Vendido",
          fecha_salida: p.fecha_salida,
          fechaVenta: p.created_at ? new Date(p.created_at) : new Date(),
          vendedor: p.vendedor_nombre || p.vendedor || "Sistema",
          cliente: [p.cliente?.nombre, p.cliente?.apellido].filter(Boolean).join(' ') || "Sin cliente",
          concepto: p.concepto || "Sin concepto",
          pasajeros: (p.adultos || 0) + (p.ninos || 0) + (p.infantes || 0),
          precioVenta: parseFloat(p.importe) || 0,
          precioCosto: 0,
          moneda: p.moneda || "USD",
          descripcion: (p.descripcion || "").replace(/\[GRUPAL:.*?\]/g, "").trim(),
          servicios: [],
          pagos: [],
          pasajerosLista: [],
          observaciones: []
        }));

      
      setProductos(paquetes);
      setProductosFiltrados(paquetes);
      setTotalProductos(Number.isFinite(Number(total)) ? Number(total) : paquetes.length);
      setCargando(false);
    } catch (error) {
      getErrorRef.current("Error al cargar los paquetes");
      setCargando(false);
    }
  }, []);

  // Si cambia la búsqueda, volvemos a la primera página
  useEffect(() => {
    setPaginationModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
  }, [terminoBusqueda]);

  // Carga desde backend (incluye búsqueda server-side)
  useEffect(() => {
    const termino = terminoBusqueda.trim();
    const timeoutId = setTimeout(() => {
      cargarProductos(paginationModel.page + 1, paginationModel.pageSize, termino);
    }, termino ? 300 : 0);

    return () => clearTimeout(timeoutId);
  }, [cargarProductos, refreshTrigger, terminoBusqueda, paginationModel]);

  // Efecto para abrir ficha automáticamente si viene openPackageId
  useEffect(() => {
    if (openPackageId) {
      handleViewProduct({ id: openPackageId });
    }
  }, [openPackageId]);

  // Cargar proveedores cuando se abre una ficha
  useEffect(() => {
    if (fichaAbierta) {
      const cargarProveedores = async () => {
        try {
          const pageSize = 500;
          let page = 1;
          let keepLoading = true;
          const acumulados: any[] = [];

          while (keepLoading) {
            const response = await proveedores.getAll(page, pageSize);
            const payload = response.data;
            const dataArr: any[] = Array.isArray(payload?.data)
              ? payload.data
              : Array.isArray(payload)
                ? payload
                : [];

            acumulados.push(...dataArr);

            const hasNext = Boolean(payload?.pagination?.hasNextPage);
            if (!hasNext || dataArr.length === 0) {
              keepLoading = false;
            } else {
              page += 1;
            }
          }

          const seen = new Set<number>();
          const lista = acumulados
            .map((p: any) => ({
              id: Number(p.id),
              nombre: (p?.nombre || '').toString().trim(),
            }))
            .filter((p) => Number.isFinite(p.id) && !!p.nombre)
            .filter((p) => {
              if (seen.has(p.id)) return false;
              seen.add(p.id);
              return true;
            })
            .sort((a, b) => a.nombre.localeCompare(b.nombre));

          setProveedoresLista(lista);
        } catch {
          setProveedoresLista([]);
        }
      };
      cargarProveedores();
    }
  }, [fichaAbierta]);

  // Handlers principales
  const handleViewProduct = async (producto: any) => {
    setProductoSeleccionado(producto);
    setFichaAbierta(true);
    
    try {
      const response = await product.getById(producto.id);
      const paqueteCompleto = response.data?.data || response.data;
      
      // Usar funciones de mapeo extraídas
      const servicios = mapAllServicios(paqueteCompleto);
      const existenteLocal = productos.find(p => p.id === producto.id) || producto;
      const pagosBackend = mapPagos(paqueteCompleto.pagosPaquetes, existenteLocal.pagos || []);
      const pasajerosBackend = mapPasajeros(paqueteCompleto, existenteLocal.pasajerosLista || []);
      
      // Parsear observaciones
      let observacionesLocal = parseObservaciones(paqueteCompleto.observaciones);
      if (observacionesLocal.length === 0 && (existenteLocal.observaciones || []).length > 0) {
        observacionesLocal = existenteLocal.observaciones;
      } else if (observacionesLocal.length === 0) {
        // Como en la BD los paquetes migrados tienen "observaciones: null", el sistema las auto-genera.
        // Asignamos el vendedor y fecha original del paquete para que no salga "Mathias" y hoy.
        const vendedorOriginal = paqueteCompleto.vendedor_nombre || paqueteCompleto.vendedor || 'SISTEMA_MIGRACION';
        let fechaOriginal = todayISO();
        try { if (paqueteCompleto.created_at) fechaOriginal = new Date(paqueteCompleto.created_at).toISOString().split('T')[0]; } catch(e){}
        
        observacionesLocal = generateAutoObservaciones(paqueteCompleto, () => vendedorOriginal, () => fechaOriginal);
      }

      // Calcular totales separados por moneda
      const totalesCargados = calcularTotalesServicios(servicios);

      setProductoSeleccionado({
        ...producto,
        fecha_salida: paqueteCompleto.fecha_salida || producto.fecha_salida,
        estado: paqueteCompleto.estado || producto.estado || "Vendido",
        servicios,
        precioCosto: totalesCargados.totalCosto,
        precioVentaUSD: totalesCargados.totalVentaUSD,
        precioVentaARS: totalesCargados.totalVentaARS,
        precioCostoUSD: totalesCargados.totalCostoUSD,
        precioCostoARS: totalesCargados.totalCostoARS,
        pagos: pagosBackend,
        pasajerosLista: pasajerosBackend,
        pasajeros: pasajerosBackend.length,
        observaciones: observacionesLocal,
        cliente_correo: paqueteCompleto.cliente?.email || paqueteCompleto.cliente?.correo || '-',
        cliente_telefono: paqueteCompleto.cliente?.telefono || paqueteCompleto.cliente?.celular || '-',
      });

      originalPaqueteRef.current = {
        concepto: (producto?.concepto ?? '').toString(),
        descripcion: (producto?.descripcion ?? '').toString(),
        precioVenta: Number(producto?.precioVenta || 0),
        moneda: (producto?.moneda ?? 'USD').toString(),
      };
    } catch (error) {
      getError('Error al cargar los servicios del paquete');
    }
  };

  const handleProductSheetClose = () => {
    setFichaAbierta(false);
    setProductoSeleccionado(null);
    if (onCloseFicha) onCloseFicha();
  };

  // Handlers para eliminación
  const handleDeleteProduct = (id: number) => {
    setProductoAEliminar(id);
    setDialogoAbierto(true);
  };

  const confirmDeleteProduct = async () => {
    try {
      if (productoAEliminar) {
        await product.delete(productoAEliminar);
        await cargarProductos(paginationModel.page + 1, paginationModel.pageSize, terminoBusqueda.trim());
        getSuccess("Paquete eliminado correctamente");
        setDialogoAbierto(false);
        setProductoAEliminar(null);
      }
    } catch (error) {
      getError("Error al eliminar el paquete");
      setDialogoAbierto(false);
    }
  };

  const handleDialogClose = () => {
    setDialogoAbierto(false);
    setProductoAEliminar(null);
  };

  const handlePrint = () => {
    if (!productoSeleccionado) return;
    printPackage(productoSeleccionado, formatearMoneda);
  };

  const guardarCambios = async () => {
    if (!productoSeleccionado) return;
    
    setGuardandoCambios(true);
    try {
      // Auto-observaciones por cambios de paquete (no pagos)
      const original = originalPaqueteRef.current;
      const observacionesActuales: any[] = Array.isArray(productoSeleccionado.observaciones)
        ? [...productoSeleccionado.observaciones]
        : [];

      if (original) {
        const cambios: any[] = [];
        const conceptoActual = (productoSeleccionado.concepto ?? '').toString();
        const descripcionActual = (productoSeleccionado.descripcion ?? '').toString();
        const monedaActual = (productoSeleccionado.moneda ?? 'USD').toString();
        

        if (conceptoActual !== (original.concepto ?? '')) {
          cambios.push(buildObs(`Actualización paquete\nConcepto: ${conceptoActual} por ${original.concepto ?? ''}`));
        }

        if (descripcionActual !== (original.descripcion ?? '')) {
          cambios.push(buildObs(`Actualización paquete\nDescripción: ${descripcionActual}`));
        }

        if (monedaActual !== (original.moneda ?? '')) {
          cambios.push(buildObs(`Actualización paquete\nMoneda: ${monedaActual} por ${original.moneda ?? ''}`));
        }
        
        if (cambios.length > 0) {
          observacionesActuales.push(...cambios);
        }
      }

      const paqueteConObservaciones = {
        ...productoSeleccionado,
        observaciones: observacionesActuales,
      };

      const payload = buildUpdatePayload(paqueteConObservaciones, observacionesActuales);
      await product.update(productoSeleccionado.id, payload);

      const productosActualizados = productos.map(p =>
        p.id === productoSeleccionado.id ? paqueteConObservaciones : p
      );
      setProductos(productosActualizados);
      setProductosFiltrados(productosActualizados);

      originalPaqueteRef.current = {
        concepto: (paqueteConObservaciones?.concepto ?? '').toString(),
        descripcion: (paqueteConObservaciones?.descripcion ?? '').toString(),
        precioVenta: Number(paqueteConObservaciones?.precioVenta || 0),
        moneda: (paqueteConObservaciones?.moneda ?? 'USD').toString(),
      };

      getSuccess("Cambios guardados exitosamente");
      setFichaAbierta(false);
    } catch (error: any) {
      const mensaje = error?.response?.data?.message || "Error al guardar los cambios";
      getError(mensaje);
    } finally {
      setGuardandoCambios(false);
    }
  };

  const persistirCostoEditadoDesdeModal = async (
    index: number,
    servicioActualizado: any,
    recalcularFinanzas = false
  ) => {
    if (!productoSeleccionado) return;

    setGuardandoCambios(true);
    try {
      const servicios = [...(productoSeleccionado.servicios || [])];
      servicios[index] = servicioActualizado;

      let paqueteActualizado: any = {
        ...productoSeleccionado,
        servicios,
      };

      if (recalcularFinanzas) {
        const totales = calcularTotalesServicios(servicios);
        paqueteActualizado = {
          ...paqueteActualizado,
          precioVenta: totales.totalVenta,
          precioCosto: totales.totalCosto,
          precioVentaUSD: totales.totalVentaUSD,
          precioVentaARS: totales.totalVentaARS,
          precioCostoUSD: totales.totalCostoUSD,
          precioCostoARS: totales.totalCostoARS,
        };
      }

      setProductoSeleccionado(paqueteActualizado);
      await persistPaqueteConSync(paqueteActualizado, {
        closeAfterSave: false,
        noEligibleMessage: "Costo guardado"
      });
    } catch (error: any) {
      const mensaje = error?.response?.data?.message || "Error al guardar costo";
      getError(mensaje);
    } finally {
      setGuardandoCambios(false);
    }
  };

  const persistirNuevoCostoDesdeModal = async (nuevoServicio: any) => {
    if (!productoSeleccionado) return;

    setGuardandoCambios(true);
    try {
      const servicios = [...(productoSeleccionado.servicios || []), nuevoServicio];
      const totales = calcularTotalesServicios(servicios);

      const paqueteActualizado: any = {
        ...productoSeleccionado,
        servicios,
        precioVenta: totales.totalVenta,
        precioCosto: totales.totalCosto,
        precioVentaUSD: totales.totalVentaUSD,
        precioVentaARS: totales.totalVentaARS,
        precioCostoUSD: totales.totalCostoUSD,
        precioCostoARS: totales.totalCostoARS,
      };

      setProductoSeleccionado(paqueteActualizado);
      await persistPaqueteConSync(paqueteActualizado, {
        closeAfterSave: false,
        noEligibleMessage: "Costo agregado"
      });
    } catch (error: any) {
      const mensaje = error?.response?.data?.message || "Error al agregar costo";
      getError(mensaje);
    } finally {
      setGuardandoCambios(false);
    }
  };

  // Utilidades
  const formatearFecha = (fecha: any) => {
    if (!fecha) return "-";
    const date = fecha instanceof Date ? fecha : new Date(fecha);
    return isNaN(date.getTime()) ? "-" : date.toLocaleDateString("es-AR", { day: "2-digit", month: "2-digit", year: "numeric" });
  };

  // Actualiza el producto seleccionado y sincroniza con la lista principal
  const actualizarProductoSeleccionado = (actualizador: (prev: any) => any) => {
    setProductoSeleccionado((prev: any) => {
      const actualizado = actualizador(prev || {});
      // Actualiza tanto la lista principal como la filtrada
      setProductos(lista => lista.map(p => p.id === actualizado.id ? actualizado : p));
      setProductosFiltrados(lista => lista.map(p => p.id === actualizado.id ? actualizado : p));
      return actualizado;
    });
  };


  // Callback para actualizar un servicio desde ServicesList
  const actualizarServicio = (index: number, servicioActualizado: any, recalcularFinanzas = false, soloRevisado = false) => {
    actualizarProductoSeleccionado((prev: any) => {
      const servicios = [...(prev?.servicios || [])];
      const servicioOriginal = servicios[index];
      servicios[index] = servicioActualizado;

      // Si hubo cambios en montos, recalcular totales financieros
      let precioVenta = prev?.precioVenta || 0;
      let precioCosto = prev?.precioCosto || 0;


      let precioVentaUSD = prev?.precioVentaUSD || 0;
      let precioVentaARS = prev?.precioVentaARS || 0;
      let precioCostoUSD = prev?.precioCostoUSD || 0;
      let precioCostoARS = prev?.precioCostoARS || 0;

      if (recalcularFinanzas) {
        const totales = calcularTotalesServicios(servicios);
        precioVenta = totales.totalVenta;
        precioCosto = totales.totalCosto;
        precioVentaUSD = totales.totalVentaUSD;
        precioVentaARS = totales.totalVentaARS;
        precioCostoUSD = totales.totalCostoUSD;
        precioCostoARS = totales.totalCostoARS;
      }

      // Generar observaciones automáticas
      let nuevasObservaciones: any[] = [];
      
      // Detectar cambios en estados de revisión
      const cambioRevisadoPrecio = servicioOriginal.revisado !== servicioActualizado.revisado;
      const cambioRevisadoCosto = servicioOriginal.revisadoCosto !== servicioActualizado.revisadoCosto;
      
      // Detectar cambios en datos
      const cambioEnDatos = 
        servicioOriginal.descripcion !== servicioActualizado.descripcion ||
        servicioOriginal.precioVenta !== servicioActualizado.precioVenta ||
        servicioOriginal.precioCosto !== servicioActualizado.precioCosto ||
        servicioOriginal.moneda !== servicioActualizado.moneda ||
        servicioOriginal.monedaCosto !== servicioActualizado.monedaCosto ||
        servicioOriginal.nroFile !== servicioActualizado.nroFile;
      
      // Siempre generar observaciones de revisión si hubo cambios
      if (cambioRevisadoPrecio) {
        const estado = servicioActualizado.revisado ? 'marcado como revisado' : 'desmarcado como revisado';
        nuevasObservaciones.push(buildObs(`Precio ${estado}\nTipo: ${servicioActualizado.tipo}\nDescripción: ${servicioActualizado.descripcion || 'N/A'}`));
      }
      
      if (cambioRevisadoCosto) {
        const estado = servicioActualizado.revisadoCosto ? 'marcado como revisado' : 'desmarcado como revisado';
        nuevasObservaciones.push(buildObs(`Costo ${estado}\nTipo: ${servicioActualizado.tipo}\nDescripción: ${servicioActualizado.descripcion || 'N/A'}`));
      }
      
      // Si hay cambios en datos (y no es solo revisión), generar observación de actualización
      if (cambioEnDatos) {
        const tipoServicio = servicioActualizado.tipo || 'Servicio';
        let descripcionLineas = [
          `Actualización ${tipoServicio.toLowerCase()}`,
          `Descripción: ${servicioActualizado.descripcion || 'N/A'}`,
        ];
        // Agregar detalles de costo si cambió
        const costoCambio = servicioOriginal.precioCosto !== servicioActualizado.precioCosto;
        if (costoCambio) {
          descripcionLineas.push(`Costo: ${servicioActualizado.monedaCosto || 'USD'} ${servicioActualizado.precioCosto || 0}`);
        }
        // Agregar detalles de venta si cambió
        const ventaCambio = servicioOriginal.precioVenta !== servicioActualizado.precioVenta;
        if (ventaCambio) {
          descripcionLineas.push(`Precio: ${servicioActualizado.moneda || 'USD'} ${servicioActualizado.precioVenta || 0}`);
        }
        // Agregar file si cambió
        const cambioFile = servicioOriginal.nroFile !== servicioActualizado.nroFile;
        if (cambioFile && servicioActualizado.nroFile) {
          descripcionLineas.push(`Nro File: ${servicioActualizado.nroFile}`);
        }
        nuevasObservaciones.push(buildObs(descripcionLineas.join('\n')));
      }

      const observaciones = [...(prev?.observaciones || []), ...nuevasObservaciones];

      return {
        ...prev,
        servicios,
        precioVenta: recalcularFinanzas ? precioVenta : prev?.precioVenta,
        precioCosto: recalcularFinanzas ? precioCosto : prev?.precioCosto,
        precioVentaUSD: recalcularFinanzas ? precioVentaUSD : prev?.precioVentaUSD,
        precioVentaARS: recalcularFinanzas ? precioVentaARS : prev?.precioVentaARS,
        precioCostoUSD: recalcularFinanzas ? precioCostoUSD : prev?.precioCostoUSD,
        precioCostoARS: recalcularFinanzas ? precioCostoARS : prev?.precioCostoARS,
        observaciones,
      };
    });

    getSuccess(recalcularFinanzas
      ? "Servicio actualizado y resumen financiero recalculado"
      : "Servicio actualizado correctamente"
    );
  };

  // Callback para agregar un nuevo servicio desde ServicesList
  const agregarServicio = (nuevoServicio: any) => {
    actualizarProductoSeleccionado((prev: any) => {
      const servicios = [...(prev?.servicios || []), nuevoServicio];
      
      // Recalcular totales separados por moneda
      const totales = calcularTotalesServicios(servicios);
      
      // Generar observación automática
      const tipoServicio = nuevoServicio.tipo || 'Servicio';
      let descripcionLineas = [
        `Nuevo ${tipoServicio.toLowerCase()} agregado`,
        `Descripción: ${nuevoServicio.descripcion || 'N/A'}`,
      ];
      if (nuevoServicio.precioVenta) {
        descripcionLineas.push(`Precio: ${nuevoServicio.moneda || 'USD'} ${nuevoServicio.precioVenta}`);
      }
      if (nuevoServicio.precioCosto) {
        descripcionLineas.push(`Costo: ${nuevoServicio.monedaCosto || 'USD'} ${nuevoServicio.precioCosto}`);
      }
      
      const observaciones = [...(prev?.observaciones || []), buildObs(descripcionLineas.join('\n'))];

      return {
        ...prev,
        servicios,
        precioVenta: totales.totalVenta,
        precioCosto: totales.totalCosto,
        precioVentaUSD: totales.totalVentaUSD,
        precioVentaARS: totales.totalVentaARS,
        precioCostoUSD: totales.totalCostoUSD,
        precioCostoARS: totales.totalCostoARS,
        observaciones,
      };
    });

    getSuccess(`${nuevoServicio.tipo || 'Servicio'} agregado correctamente`);
  };

  // Callback para eliminar un servicio desde ServicesList
  const eliminarServicio = (index: number, servicioEliminado: any) => {
    actualizarProductoSeleccionado((prev: any) => {
      const servicios = [...(prev?.servicios || [])];
      servicios.splice(index, 1);
      
      // Recalcular totales separados por moneda
      const totales = calcularTotalesServicios(servicios);
      
      // Generar observación automática
      const tipoServicio = servicioEliminado.tipo || 'Servicio';
      let descripcionLineas = [
        `❌ ${tipoServicio} eliminado`,
        `Descripción: ${servicioEliminado.descripcion || 'N/A'}`,
      ];
      
      const observaciones = [...(prev?.observaciones || []), buildObs(descripcionLineas.join('\n'))];

      return {
        ...prev,
        servicios,
        precioVenta: totales.totalVenta,
        precioCosto: totales.totalCosto,
        precioVentaUSD: totales.totalVentaUSD,
        precioVentaARS: totales.totalVentaARS,
        precioCostoUSD: totales.totalCostoUSD,
        precioCostoARS: totales.totalCostoARS,
        observaciones,
      };
    });

    getSuccess(`${servicioEliminado.tipo || 'Servicio'} eliminado (requiere Guardar)`);
  };

  // Función helper para calcular totales de servicios separados por moneda
  const calcularTotalesServicios = (servicios: any[], _monedaPaquete?: string) => {
    let totalVentaUSD = 0;
    let totalVentaARS = 0;
    let totalCostoUSD = 0;
    let totalCostoARS = 0;
    
    servicios.forEach((s: any) => {
      const monedaVenta = (s.moneda || 'USD').toUpperCase();
      const monedaCosto = (s.monedaCosto || 'USD').toUpperCase();
      
      if (monedaVenta === 'USD') {
        totalVentaUSD += s.precioVenta || 0;
      } else {
        totalVentaARS += s.precioVenta || 0;
      }
      
      if (monedaCosto === 'USD') {
        totalCostoUSD += s.precioCosto || 0;
      } else {
        totalCostoARS += s.precioCosto || 0;
      }
    });
    
    // Retornar los 4 totales separados por moneda
    return {
      totalVentaUSD,
      totalVentaARS,
      totalCostoUSD,
      totalCostoARS,
      // Compatibilidad: totalVenta/totalCosto con el mayor o USD por defecto
      totalVenta: totalVentaUSD + totalVentaARS,
      totalCosto: totalCostoUSD + totalCostoARS,
    };
  };


  // Callback para actualizar lista de pasajeros cuando la sección agrega uno
  const onAddPassenger = (nuevo: any) => {
    actualizarProductoSeleccionado((prev: any) => {
      const pasajerosLista = [...(prev?.pasajerosLista || []), nuevo];
      const nombre = (nuevo?.nombre || '').toString().trim();
      const descripcion = `Se agregó pasajero\nPasajero: ${nombre || 'N/A'}`;
      const observaciones = [...(prev?.observaciones || []), buildObs(descripcion)];
      return { ...prev, pasajerosLista, pasajeros: pasajerosLista.length, observaciones };
    });
    setNuevoPasajero({ nombre: "", dni: "", pasaporte: "", fechaNacimiento: "" });
    getSuccess("Pasajero agregado correctamente");
  };

  // Callback para actualizar un pasajero editado
  const onUpdatePassenger = (pasajeroEditado: any) => {
    actualizarProductoSeleccionado((prev: any) => {
      const pasajerosLista = (prev?.pasajerosLista || []).map((p: any) => 
        p.id === pasajeroEditado.id ? pasajeroEditado : p
      );
      const nombre = (pasajeroEditado?.nombre || '').toString().trim();
      const descripcion = `Actualización pasajero\nPasajero: ${nombre || 'N/A'}`;
      const observaciones = [...(prev?.observaciones || []), buildObs(descripcion)];
      return { ...prev, pasajerosLista, pasajeros: pasajerosLista.length, observaciones };
    });
    getSuccess("Pasajero editado correctamente");
  };

  const eliminarPasajero = async (pasajero: any) => {
    if (!productoSeleccionado?.id || !pasajero?.id) return;
    
    try {
      await product.deletePasajero(productoSeleccionado.id, pasajero.id);
      
      actualizarProductoSeleccionado((prev: any) => {
        const pasajerosLista = (prev?.pasajerosLista || []).filter((p: any) => p.id !== pasajero.id);
        const nombre = (pasajero?.nombre || '').toString().trim();
        const descripcion = `Se eliminó pasajero\nPasajero: ${nombre || 'N/A'}`;
        const observaciones = [...(prev?.observaciones || []), buildObs(descripcion)];
        return { ...prev, pasajerosLista, pasajeros: pasajerosLista.length, observaciones };
      });
      
      getSuccess("Pasajero eliminado correctamente");
    } catch (error: any) {
      const mensaje = error?.response?.data?.message || "Error al eliminar el pasajero";
      getError(mensaje);
    }
  };

  // Funciones para manejo de pagos
  const agregarPago = async () => {
    if (!productoSeleccionado?.id || !nuevoPago.concepto || !nuevoPago.monto) return;
    
    try {
      const montoOriginal = parseFloat(nuevoPago.monto);
      const tipoCambioIngresado = nuevoPago.tipoCambio ? parseFloat(nuevoPago.tipoCambio) : null;
      // tipoCambio real: solo si es > 0 y distinto de 1
      const tipoCambioReal = (tipoCambioIngresado && tipoCambioIngresado > 0 && tipoCambioIngresado !== 1) ? tipoCambioIngresado : null;
      
      // En el sistema dual, cada pago se registra en su propia moneda.
      // Si se ingresa un tipo de cambio, el pago descuenta de la moneda OPUESTA (convertido).
      const pagoData = {
        observacion: nuevoPago.concepto,
        moneda: nuevoPago.moneda,
        importe: montoOriginal,
        tipoCambio: tipoCambioReal,
        montoConvertido: null,
        responsable_nombre: nuevoPago.responsable_nombre || '',
        revisado_por_nombre: nuevoPago.revisado_por_nombre || '',
      };

      const response = await product.createPago(productoSeleccionado.id, pagoData);

      actualizarProductoSeleccionado((prev: any) => {
        const pagos = [...(prev?.pagos || []), response.data];
        
        // Agregar observación automática de creación
        const obsPago = buildObs([
          'Nuevo pago registrado',
          `Concepto: ${pagoData.observacion}`,
          `Monto: ${pagoData.moneda} ${pagoData.importe}`,
          ...(pagoData.revisado_por_nombre ? [`Revisado por: ${pagoData.revisado_por_nombre}`] : [])
        ].join('\n'));

        const observaciones = [...(prev?.observaciones || []), obsPago];
        return { ...prev, pagos, observaciones };
      });

      // Persistir las observaciones en el backend para que no se pierdan si no se da "Guardar Cambios"
      const observacionesActuales = productoSeleccionado.observaciones || [];
      const nuevasObservaciones = [...observacionesActuales, buildObs([
          'Nuevo pago registrado',
          `Concepto: ${pagoData.observacion}`,
          `Monto: ${pagoData.moneda} ${pagoData.importe}`,
          ...(pagoData.revisado_por_nombre ? [`Revisado por: ${pagoData.revisado_por_nombre}`] : [])
        ].join('\n'))];
      
      await product.update(productoSeleccionado.id, {
        observaciones: JSON.stringify(nuevasObservaciones)
      });

      setNuevoPago({ concepto: "", moneda: "USD", monto: "", tipoCambio: "", responsable_nombre: getCurrentUsername(), revisado_por_nombre: "" });
      getSuccess("Pago agregado correctamente");
    } catch (error: any) {
      getError(error.response?.data?.message || "Error al agregar el pago");
    }
  };

  const editarPago = async (index: number, pagoEditado: any) => {
    if (!productoSeleccionado?.id) return;
    
    const pago = productoSeleccionado.pagos[index];
    if (!pago?.id) {
      getError("El pago no tiene ID");
      return;
    }

    try {
      const response = await product.updatePago(productoSeleccionado.id, pago.id, pagoEditado);
      
      // Normalizar valores para comparación (undefined, null, '' son equivalentes)
      const normalize = (val: any) => val || '';
      
      // Detectar si solo cambió "Revisado Por"
      const cambioRevisadoPor = normalize(pago.revisado_por_nombre) !== normalize(pagoEditado.revisado_por_nombre);
      const cambioResponsable = normalize(pago.responsable_nombre) !== normalize(pagoEditado.responsable_nombre);
      const cambioConcepto = pago.observacion !== pagoEditado.observacion;
      const cambioMoneda = pago.moneda !== pagoEditado.moneda;
      const cambioImporte = pago.importe !== pagoEditado.importe;
      const cambioTipoCambio = pago.tipoCambio !== pagoEditado.tipoCambio;
      
      const soloRevisadoPor = cambioRevisadoPor && !cambioResponsable && !cambioConcepto && !cambioMoneda && !cambioImporte && !cambioTipoCambio;
      
      // Generar observación automática
      let descripcion: string;
      
      if (soloRevisadoPor) {
        // Observación específica para cambio de Revisado Por
        descripcion = [
          'Pago marcado como revisado',
          `Concepto: ${pagoEditado.observacion || 'Sin concepto'}`,
          `Revisado por: ${pagoEditado.revisado_por_nombre || 'N/A'}`,
        ].join('\n');
      } else {
        // Observación completa para edición de pago
        const lines = [
          'Actualización pago',
          `Concepto: ${pagoEditado.observacion || 'Sin concepto'}`,
          `Importe: ${pagoEditado.moneda || 'USD'} ${pagoEditado.importe || 0}`,
        ];
        
        // Si también cambió revisado_por, mencionarlo
        if (cambioRevisadoPor && pagoEditado.revisado_por_nombre) {
          lines.push(`Revisado por: ${pagoEditado.revisado_por_nombre}`);
        }

        descripcion = lines.join('\n');
      }
      
      actualizarProductoSeleccionado((prev: any) => {
        const pagos = [...(prev?.pagos || [])];
        pagos[index] = response.data;
        const observaciones = [...(prev?.observaciones || []), buildObs(descripcion)];
        return { ...prev, pagos, observaciones };
      });
      
      // Persistir observaciones en el backend inmediatamente
      const observacionesAlMomento = [...(productoSeleccionado.observaciones || []), buildObs(descripcion)];
      await product.update(productoSeleccionado.id, {
        observaciones: JSON.stringify(observacionesAlMomento)
      });

      getSuccess("Pago actualizado correctamente");
    } catch (error: any) {
      getError(error.response?.data?.message || "Error al actualizar el pago");
    }
  };

  const eliminarPago = async (index: number) => {
    if (!productoSeleccionado?.id) return;
    
    const pago = productoSeleccionado.pagos[index];
    if (!pago?.id) {
      getError("El pago no tiene ID");
      return;
    }

    try {
      await product.deletePago(productoSeleccionado.id, pago.id);
      
      actualizarProductoSeleccionado((prev: any) => {
        const pagos = [...(prev?.pagos || [])];
        pagos.splice(index, 1);
        return { ...prev, pagos };
      });

      getSuccess("Pago eliminado correctamente");
    } catch (error: any) {
      getError(error.response?.data?.message || "Error al eliminar el pago");
    }
  };

  // Toggle de Revisado Por en pagos (como Switch)
  const toggleRevisadoPago = async (index: number, isChecked: boolean) => {
    if (!productoSeleccionado?.id) return;
    
    const pago = productoSeleccionado.pagos[index];
    if (!pago?.id) {
      getError("El pago no tiene ID");
      return;
    }

    try {
      const currentUser = getCurrentUsername();
      const nuevoRevisadoPor = isChecked ? currentUser : '';
      
      const pagoEditado = {
        observacion: pago.observacion,
        moneda: pago.moneda,
        importe: pago.importe,
        tipoCambio: pago.tipoCambio,
        responsable_nombre: pago.responsable_nombre,
        revisado_por_nombre: nuevoRevisadoPor,
      };

      const response = await product.updatePago(productoSeleccionado.id, pago.id, pagoEditado);
      
      // Generar observación automática
      const descripcion = isChecked
        ? [
            'Pago marcado como revisado',
            `Concepto: ${pago.observacion || 'Sin concepto'}`,
            `Revisado por: ${currentUser}`,
          ].join('\n')
        : [
            'Pago desmarcado como revisado',
            `Concepto: ${pago.observacion || 'Sin concepto'}`,
          ].join('\n');
      
      actualizarProductoSeleccionado((prev: any) => {
        const pagos = [...(prev?.pagos || [])];
        pagos[index] = response.data;
        const observaciones = [...(prev?.observaciones || []), buildObs(descripcion)];
        return { ...prev, pagos, observaciones };
      });
      
      // Persistir observaciones en el backend inmediatamente
      const observacionesAlMomento = [...(productoSeleccionado.observaciones || []), buildObs(descripcion)];
      await product.update(productoSeleccionado.id, {
        observaciones: JSON.stringify(observacionesAlMomento)
      });

      getSuccess(isChecked ? "Pago marcado como revisado" : "Pago desmarcado");
    } catch (error: any) {
      getError(error.response?.data?.message || "Error al actualizar el pago");
    }
  };

  // Funciones para manejo de observaciones
  const agregarObservacion = () => {
    if (!productoSeleccionado || !nuevaObservacion.descripcion) return;
    const observacion = {
      ...nuevaObservacion,
      fecha: nuevaObservacion.fecha || new Date().toISOString().split('T')[0],
      usuario: (nuevaObservacion.usuario || '').trim() || getCurrentUsername()
    };
    actualizarProductoSeleccionado((prev: any) => {
      const observaciones = [...(prev?.observaciones || []), observacion];
      return { ...prev, observaciones };
    });
    setNuevaObservacion({ descripcion: "", fecha: "", usuario: getCurrentUsername() });
  };



  const editarObservacion = (index: number, nueva: any) => {
    actualizarProductoSeleccionado((prev: any) => {
      const observaciones = [...(prev?.observaciones || [])];
      observaciones[index] = nueva;
      return { ...prev, observaciones };
    });
    getSuccess("Observación editada");
  };

  // Función para cambiar el estado del paquete
  const cambiarEstado = async (nuevoEstado: string) => {
    if (!productoSeleccionado?.id) return;
    
    try {
      await product.updateEstado(productoSeleccionado.id, nuevoEstado, getCurrentUsername());
      
      // Actualizar estado local del producto seleccionado
      actualizarProductoSeleccionado((prev: any) => ({
        ...prev,
        estado: nuevoEstado
      }));
      
      // Actualizar la fila en la tabla también
      setProductosFiltrados((filasPrevias: any[]) => 
        filasPrevias.map((fila: any) => 
          fila.id === productoSeleccionado.id 
            ? { ...fila, estado: nuevoEstado } 
            : fila
        )
      );
      
      getSuccess(`Estado cambiado a ${nuevoEstado}`);
    } catch (error) {
      getError("Error al cambiar el estado");
    }
  };

  // Helper para celdas largas con Tooltip
  const renderTooltipCell = (params: any) => (
    <Tooltip title={params.value || ""} placement="top-start" arrow>
      <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", display: "block", width: "100%" }}>{params.value}</span>
    </Tooltip>
  );

  // Configuración de columnas para la tabla
  const columnas: GridColDef[] = [
    {
      field: "actions",
      headerName: "Acciones",
      width: 120,
      align: "center",
      headerAlign: "center",
      disableColumnMenu: true,
      renderCell: (params) => (
        <Box sx={{ display: "flex", gap: 1 }}>
          <IconButton size="small" color="primary" onClick={() => handleViewProduct(params.row)} title="Ver ficha del producto">
            <Visibility />
          </IconButton>
          <IconButton 
            size="small" 
            sx={{ color: '#ff9800' }}
            onClick={async () => {
              try {
                // Secuencia de estados para avanzar
                const secuencia = ["Pendiente", "Enviado", "Vendido", "Revisión Operativa", "Revisado", "Finalizado"];
                const estadoActual = params.row.estado || "Vendido";
                const indiceActual = secuencia.indexOf(estadoActual);
                
                // Si está en el último estado (Finalizado), no avanza más
                if (indiceActual >= secuencia.length - 1) {
                  getSuccess(`Paquete #${params.row.id} ya está Finalizado`);
                  return;
                }
                
                const siguienteEstado = secuencia[indiceActual + 1];
                await product.updateEstado(params.row.id, siguienteEstado);
                
                // Actualizar estado local
                const productosActualizados = productos.map(p => 
                  p.id === params.row.id ? { ...p, estado: siguienteEstado } : p
                );
                setProductos(productosActualizados);
                setProductosFiltrados(productosActualizados);
                getSuccess(`Paquete #${params.row.id} cambiado a ${siguienteEstado}`);
              } catch (error) {
                getError("Error al cambiar el estado");
              }
            }} 
            title="Avanzar al siguiente estado"
          >
            <Refresh />
          </IconButton>
          <IconButton size="small" color="error" onClick={() => handleDeleteProduct(params.row.id)} title="Eliminar">
            <Delete />
          </IconButton>
        </Box>
      ),
    },
    { field: "numero", headerName: "#", width: 80, align: "center", headerAlign: "center", disableColumnMenu: true },
    {
      field: "estado",
      headerName: "Estado",
      width: 120,
      align: "center",
      headerAlign: "center",
      disableColumnMenu: true,
      renderCell: (params) => {
        const colores: Record<string, string> = {
          "Vendido": "#4caf50",
          "Enviado": "#4facfe",
          "Pendiente": "#ff6b6b",
          "Revisión Operativa": "#ff82ab",
          "Revisado": "#764ba2",
          "Finalizado": "#2e7d32"
        };
        // Acortar labels largos para que quepan en el chip
        const labelCorto: Record<string, string> = {
          "Revisión Operativa": "Rev. Op.",
        };
        const label = labelCorto[params.value] || params.value;
        const color = colores[params.value] || "#4caf50";
        return (
          <span style={{ backgroundColor: color, color: "white", padding: "4px 8px", borderRadius: "12px", fontSize: "12px", fontWeight: "bold" }}>
            {label}
          </span>
        );
      },
    },
    { field: "fechaVenta", headerName: "Fecha de venta", width: 140, align: "center", headerAlign: "center", disableColumnMenu: true, renderCell: (params) => formatearFecha(params.value) },
    { field: "vendedor", headerName: "Vendedor", width: 180, align: "left", headerAlign: "left", disableColumnMenu: true, renderCell: renderTooltipCell },
    { field: "cliente", headerName: "Cliente", width: 250, align: "left", headerAlign: "left", disableColumnMenu: true, renderCell: renderTooltipCell },
    { field: "concepto", headerName: "Concepto", width: 300, align: "left", headerAlign: "left", disableColumnMenu: true, renderCell: renderTooltipCell },
  ];

  // Pantalla de carga optimizada
  if (cargando) {
    return (
      <Box sx={{ 
        display: "flex", 
        flexDirection: "column",
        justifyContent: "center", 
        alignItems: "center", 
        height: "50vh",
        gap: 2
      }}>
        <CircularProgress size={40} />
        <Typography variant="body2" color="text.secondary">
          Cargando productos...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ pl: -2, mt: 3 }}>
      {/* Header con título */}
      <Box sx={{ mb: 3, mt: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Gestión de Paquetes</Typography>
      </Box>

      {/* Tabla de productos o mensaje de estado vacío */}
      {productosFiltrados.length > 0 ? (
        <TableComponent
          columnData={columnas}
          rowData={productosFiltrados}
          actions={false}
          actionsType={"products"}
          exportTitle={"Listado de Paquetes"}
          hideSearch={true}
          serverPagination={true}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          rowCount={totalProductos}
          loading={cargando}
        />
      ) : (
        <Paper elevation={1} sx={{ p: 4, textAlign: "center", borderRadius: 2 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>No se encontraron paquetes</Typography>
          <Typography variant="body2" color="text.secondary">
            {terminoBusqueda ? "No hay paquetes que coincidan con tu búsqueda" : "No hay paquetes registrados en el sistema"}
          </Typography>
        </Paper>
      )}

      {/* Modal de Ficha del Producto */}
      <Dialog 
        open={fichaAbierta} 
        onClose={handleProductSheetClose} 
        maxWidth="xl" 
        fullWidth
        sx={{ '& .MuiDialog-paper': { m: { xs: 1, sm: 2 }, maxHeight: { xs: '95vh', sm: '90vh' } } }}
      >
        <DialogTitle sx={{ p: 0, borderBottom: 1, borderColor: 'divider' }}>
          <Box sx={{ p: 3, textAlign: 'center', backgroundColor: '#f5f5f5' }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 2 }}>
              <img src={logo} alt="YO VOY" style={{ height: '60px'}} />
            </Box>
            <TextField
              variant="standard"
              value={productoSeleccionado?.concepto || ""}
              onChange={(e) => actualizarProductoSeleccionado((prev: any) => ({ ...prev, concepto: e.target.value }))}
              InputProps={{
                endAdornment: (
                  <Edit sx={{ color: 'text.secondary', opacity: 0.4, fontSize: '1.2rem' }} />
                )
              }}
              sx={{
                mb: 1,
                '& .MuiInputBase-input': {
                  textAlign: 'center',
                  fontSize: '1.5rem',
                  fontWeight: 600,
                  color: '#000000'
                }
              }}
              fullWidth
            />
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
              <Chip label={`#${productoSeleccionado?.numero}`} color="error" variant="filled" sx={{ fontWeight: 600 }} />
              <Chip label={productoSeleccionado?.estado} color="success" variant="filled" sx={{ fontWeight: 600 }} />
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          <Grid container spacing={3}>
            {/* Resumen Financiero */}
            <FinancialSummary 
              precioVentaUSD={productoSeleccionado?.precioVentaUSD || 0}
              precioVentaARS={productoSeleccionado?.precioVentaARS || 0}
              precioCostoUSD={productoSeleccionado?.precioCostoUSD || 0}
              precioCostoARS={productoSeleccionado?.precioCostoARS || 0}
              formatearMoneda={formatearMoneda}
            />

            {/* Costos y Precios en un único listado */}
            <ServicesList 
              servicios={productoSeleccionado?.servicios || []}
              monedaPaquete={productoSeleccionado?.moneda || 'USD'}
              fechaViaje={productoSeleccionado?.fecha_salida}
              formatearMoneda={formatearMoneda}
              onUpdateServicio={actualizarServicio}
              onAddServicio={agregarServicio}
              onDeleteServicio={eliminarServicio}
              onPersistCostoEdit={persistirCostoEditadoDesdeModal}
              onPersistNuevoCosto={persistirNuevoCostoDesdeModal}
              paqueteId={productoSeleccionado?.id}
              proveedores={proveedoresLista}
            />

            {/* Descripción */}
            <Grid item xs={12}>
              <Paper elevation={1} sx={{ p: 3, border: '1px solid #e0e0e0' }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>DESCRIPCIÓN</Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  value={productoSeleccionado?.descripcion || ""}
                  onChange={(e) => actualizarProductoSeleccionado((prev: any) => ({ ...prev, descripcion: e.target.value }))}
                  size="small"
                  placeholder="Ingrese una descripción..."
                />
              </Paper>
            </Grid>

            {/* Listado de Pagos */}
            <PaymentsSection
              precioVentaUSD={productoSeleccionado?.precioVentaUSD || 0}
              precioVentaARS={productoSeleccionado?.precioVentaARS || 0}
              pagos={productoSeleccionado?.pagos || []}
              nuevoPago={nuevoPago}
              setNuevoPago={setNuevoPago}
              agregarPago={agregarPago}
              editarPago={editarPago}
              eliminarPago={eliminarPago}
              onToggleRevisadoPago={toggleRevisadoPago}
              formatearMoneda={formatearMoneda}
              getCurrentUsername={getCurrentUsername}
              paqueteId={productoSeleccionado?.id}
            />

            {/* Información del Cliente - Horizontal */}
            <Grid item xs={12}>
              <Paper elevation={1} sx={{ p: 2, border: '1px solid #e0e0e0' }}>
                <Grid container spacing={3} alignItems="center" justifyContent="space-between">
                  <Grid item xs={2}>
                    <Typography variant="body2" color="text.secondary">Cliente:</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>{productoSeleccionado?.cliente || "N/A"}</Typography>
                  </Grid>
                  <Grid item xs={2}>
                    <Typography variant="body2" color="text.secondary">Correo:</Typography>
                    <Typography variant="body1">{productoSeleccionado?.cliente_correo || "-"}</Typography>
                  </Grid>
                  <Grid item xs={2}>
                    <Typography variant="body2" color="text.secondary">Teléfono:</Typography>
                    <Typography variant="body1">{productoSeleccionado?.cliente_telefono || "-"}</Typography>
                  </Grid>
                  <Grid item xs={2}>
                    <Typography variant="body2" color="text.secondary">Vendedor:</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>{productoSeleccionado?.vendedor || "N/A"}</Typography>
                  </Grid>
                  <Grid item xs={2}>
                    <Typography variant="body2" color="text.secondary">Fecha Salida:</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {productoSeleccionado?.fecha_salida ? formatearFecha(productoSeleccionado.fecha_salida) : "N/A"}
                    </Typography>
                  </Grid>
                  <Grid item xs={2}>
                    <Typography variant="body2" color="text.secondary">Estado:</Typography>
                    <Chip label={productoSeleccionado?.estado || "N/A"} color="success" variant="filled" size="small" sx={{ fontWeight: 600 }} />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            <PassengersSection 
              pasajeros={productoSeleccionado?.pasajerosLista || []}
              nuevoPasajero={nuevoPasajero}
              setNuevoPasajero={setNuevoPasajero}
              eliminarPasajero={eliminarPasajero}
              paqueteId={productoSeleccionado?.id}
              onAddPassenger={onAddPassenger}
              onUpdatePassenger={onUpdatePassenger}
            />

            <ObservationsSection 
              observaciones={productoSeleccionado?.observaciones || []}
              nuevaObservacion={nuevaObservacion}
              setNuevaObservacion={setNuevaObservacion}
              agregarObservacion={agregarObservacion}
              editarObservacion={editarObservacion}
            />
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 3, borderTop: 1, borderColor: 'divider', justifyContent: 'space-between' }}>
          <Button onClick={handleProductSheetClose} variant="outlined">Cerrar</Button>
          
          {/* Botones de cambio de estado */}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center', flex: 1, alignItems: 'center' }}>
            
            {/* Pendiente: solo puede avanzar a Enviado */}
            {productoSeleccionado?.estado === "Pendiente" && (
              <Button 
                variant="contained" 
                size="small"
                color="primary"
                onClick={() => cambiarEstado("Enviado")}
              >
                → Enviar a Enviado
              </Button>
            )}
            
            {/* Enviado: volver a Pendiente o avanzar a Vendido */}
            {productoSeleccionado?.estado === "Enviado" && (
              <>
                <Button 
                  variant="outlined" 
                  size="small"
                  color="primary"
                  onClick={() => cambiarEstado("Pendiente")}
                >
                  ↩ Volver a Pendiente
                </Button>
                <Button 
                  variant="contained" 
                  size="small"
                  color="primary"
                  onClick={() => cambiarEstado("Vendido")}
                >
                  → Pasar a Vendido
                </Button>
              </>
            )}
            
            {/* Vendido: volver a Enviado o avanzar a Rev Op */}
            {productoSeleccionado?.estado === "Vendido" && (
              <>
                <Button 
                  variant="outlined" 
                  size="small"
                  color="primary"
                  onClick={() => cambiarEstado("Enviado")}
                >
                  ↩ Volver a Enviado
                </Button>
                <Button 
                  variant="contained" 
                  size="small"
                  color="primary"
                  onClick={() => cambiarEstado("Revisión Operativa")}
                >
                  → Enviar a Revisión Operativa
                </Button>
              </>
            )}
            
            {/* Revisión Operativa: volver a Vendido o avanzar a Revisado */}
            {productoSeleccionado?.estado === "Revisión Operativa" && (
              <>
                <Button 
                  variant="outlined" 
                  size="small"
                  color="primary"
                  onClick={() => cambiarEstado("Vendido")}
                >
                  ↩ Volver a Vendido
                </Button>
                <Button 
                  variant="contained" 
                  size="small"
                  color="primary"
                  onClick={() => cambiarEstado("Revisado")}
                >
                  → Pasar a Revisado
                </Button>
              </>
            )}
            
            {/* Revisado: volver a Rev Op o avanzar a Finalizado */}
            {productoSeleccionado?.estado === "Revisado" && (
              <>
                <Button 
                  variant="outlined" 
                  size="small"
                  color="primary"
                  onClick={() => cambiarEstado("Revisión Operativa")}
                >
                  ↩ Volver a Rev. Op.
                </Button>
                <Button 
                  variant="contained" 
                  size="small"
                  color="success"
                  onClick={() => cambiarEstado("Finalizado")}
                >
                  ✓ Finalizar
                </Button>
              </>
            )}
            
            {/* Finalizado: estado final, solo puede volver a Revisado */}
            {productoSeleccionado?.estado === "Finalizado" && (
              <>
                <Button 
                  variant="outlined" 
                  size="small"
                  color="primary"
                  onClick={() => cambiarEstado("Revisado")}
                >
                  ↩ Volver a Revisado
                </Button>
                <Chip label="✓ Paquete Finalizado" color="success" />
              </>
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button 
              variant="contained" 
              color="success" 
              startIcon={guardandoCambios ? <CircularProgress size={20} color="inherit" /> : <Save />} 
              onClick={guardarCambios}
              disabled={guardandoCambios || sincronizandoProveedores}
            >
              {guardandoCambios ? 'Guardando...' : 'Guardar Cambios'}
            </Button>
            <Button variant="contained" startIcon={<Print />} onClick={handlePrint}>Imprimir</Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Dialog de confirmación para eliminar */}
      <Dialog open={dialogoAbierto} onClose={handleDialogClose} aria-labelledby="alert-dialog-title" aria-describedby="alert-dialog-description">
        <DialogTitle id="alert-dialog-title">Eliminar Paquete</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            ¿Está seguro que desea eliminar este paquete? Esta acción no se puede deshacer.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancelar</Button>
          <Button onClick={confirmDeleteProduct} color="error" autoFocus>Eliminar</Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Pagos - DESHABILITADO (pendiente definición de campos) */}
    </Box>
  );
};

export default Productos;